<template>
  <div class="product-card" :class="isRegistrant ? 'registrant-product-card' : 'gift-giver-product-card'">
    <div
      class="product-thumbnail-wrapper-container"
      role="button"
      tabindex="0"
      @click="openGifCardSlideout"
      @keydown.enter="openGifCardSlideout"
      @keydown.space.prevent="openGifCardSlideout"
    >
      <div class="product-image">
        <AtomicUiPicture
          :image-url="giftCardProductCard.imageUrl"
          :image-intrinsic-width="302"
          :aspect-ratio="0.8"
          :using-scene7="false"
          :pt="{ img: { fetchpriority: 'high', loading: 'eager' } }"
        />
      </div>
      <div class="product-description">
        <div class="product-spacing">
          <div class="medium product-brand">
            {{ giftCardProductCard.brand }}
          </div>
          <div class="medium product-name">Gift Card</div>
        </div>
        <div class="heavy product-info">
          {{ productInfo }}
        </div>
      </div>
    </div>
    <div class="product-card-footer">
      <div v-if="isRegistrant" class="footer-description grid-x medium" data-fs-mask>${{ totalPurchasedValue }} total purchased</div>
      <div class="grid-x cta-container">
        <AtomicUiButton :variant="isRegistrant ? 'secondary' : 'primary'" :expanded="true" @click="openGifCardSlideout">
          {{ cta }}
        </AtomicUiButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { giftCardProductCard } from 'config';
import { useGiftCardStore } from '~/stores/giftCard';

const props = defineProps({
  productItem: {
    type: Object,
    required: true,
  },
  isRegistrant: {
    type: Boolean,
    required: false,
    default: false,
  },
  totalPurchasedValue: {
    type: Number,
    required: false,
    default: 0,
  },
});
const emit = defineEmits(['openGifCardSlideout']);

useHead(() => ({
  link: [
    {
      rel: 'preload',
      as: 'image',
      type: 'image/jpeg',
      href: giftCardProductCard.imageUrl,
      fetchpriority: 'high',
    },
  ],
}));

const giftCardStore = useGiftCardStore();
const productInfo = props.isRegistrant ? giftCardProductCard.registrantInfo : giftCardProductCard.giftGiverInfo;
const cta = props.isRegistrant ? giftCardProductCard.registrantCTA : giftCardProductCard.giftGiverCTA;

const openGifCardSlideout = () => {
  if (props.isRegistrant) {
    giftCardStore.showGiftCardOptionsSlideout({ refresh: true });
  } else {
    emit('openGifCardSlideout', props.productItem);
  }
};
</script>
<style lang="scss" scoped>
.product-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  .product-image {
    padding-bottom: initial;
    cursor: pointer;
  }
  .product-spacing {
    .product-brand,
    .product-name {
      cursor: pointer;
    }
    &:hover {
      .product-brand,
      .product-name {
        text-decoration: underline;
      }
    }
  }
  .product-info {
    margin-top: rem-calc(20);
  }
}
.gift-giver-product-card {
  @media print {
    page-break-inside: avoid;
    margin-top: rem-calc(24);
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
@mixin align-end {
  align-items: end;
  display: grid;
}
@include breakpoint(small down) {
  .product-description {
    height: rem-calc(203);
    margin-top: rem-calc(4);
  }
  .gift-giver-product-card {
    .product-card-footer {
      height: rem-calc(169);
      margin-top: rem-calc(12);
      @include align-end;
    }
  }
  .registrant-product-card {
    .product-card-footer {
      height: rem-calc(160);
      margin-top: rem-calc(12);
      .footer-description {
        height: rem-calc(40);
        margin-bottom: rem-calc(8);
        @include align-end;
      }
    }
  }
}
@include breakpoint(medium only) {
  .product-description {
    height: rem-calc(176);
    margin-top: rem-calc(4);
  }
  .gift-giver-product-card {
    .product-card-footer {
      height: rem-calc(164);
      margin-top: rem-calc(12);
      @include align-end;
    }
  }
  .registrant-product-card {
    .product-card-footer {
      height: rem-calc(164);
      margin-top: rem-calc(12);
      .footer-description {
        height: rem-calc(40);
        margin-bottom: rem-calc(12);
        @include align-end;
      }
    }
  }
}
@include breakpoint(large up) {
  .product-description {
    height: rem-calc(167);
    margin-top: 0;
  }
  .gift-giver-product-card {
    .product-card-footer {
      height: rem-calc(91);
      margin-top: rem-calc(12);
      @include align-end;
      .cta-container {
        padding-right: rem-calc(40);
      }
    }
  }
  .registrant-product-card {
    .product-card-footer {
      margin-top: auto;
      .footer-description {
        height: auto;
        margin-bottom: rem-calc(12);
        margin-top: rem-calc(12);
      }
      .cta-container {
        padding-right: rem-calc(62);
      }
    }
  }
}
@media print {
  .gift-giver-product-card {
    .product-thumbnail-wrapper-container {
      min-height: rem-calc(480);
      height: 100%;
    }
    .product-card-footer {
      min-height: rem-calc(91);
      height: 100%;
    }
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
@mixin align-end {
  align-items: end;
  display: grid;
}
@include breakpoint(small down) {
  .product-description {
    height: rem-calc(171);
    margin-top: rem-calc(4);
  }
  .gift-giver-product-card {
    .product-card-footer {
      height: rem-calc(156);
      margin-top: rem-calc(12);
      @include align-end;
    }
  }
  .registrant-product-card {
    .product-card-footer {
      height: rem-calc(168);
      margin-top: auto;
      .footer-description {
        height: rem-calc(48);
        margin-bottom: rem-calc(8);
        align-items: end;
      }
    }
  }
}
@include breakpoint(medium only) {
  .product-description {
    height: rem-calc(136);
    margin-top: rem-calc(4);
  }
  .gift-giver-product-card {
    .product-card-footer {
      height: rem-calc(164);
      margin-top: rem-calc(12);
      @include align-end;
    }
  }
  .registrant-product-card {
    .product-description {
      height: rem-calc(150);
    }
    .product-card-footer {
      height: rem-calc(172);
      .footer-description {
        height: rem-calc(48);
        margin-bottom: rem-calc(12);
        @include align-end;
      }
    }
  }
}
@include breakpoint(large up) {
  .product-description {
    height: rem-calc(143);
    margin-top: 0;
  }
  .gift-giver-product-card {
    .product-card-footer {
      height: rem-calc(89);
      margin-top: rem-calc(12);
      @include align-end;
      .cta-container {
        padding-right: rem-calc(40);
      }
    }
  }
  .registrant-product-card {
    .product-description {
      height: rem-calc(141);
    }
    .product-card-footer {
      margin-top: auto;
      .footer-description {
        height: auto;
        margin-top: rem-calc(12);
        margin-bottom: rem-calc(12);
      }
    }
  }
}
@media print {
  .gift-giver-product-card {
    .product-thumbnail-wrapper-container {
      min-height: rem-calc(480);
      height: 100%;
    }
    .product-card-footer {
      min-height: rem-calc(109);
      height: 100%;
    }
  }
}
</style>
